<script lang="ts" setup>
    import { onMounted, reactive } from 'vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import TextInput from '../../components/TextInput.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue';

    import { MessageSectionType, DisplayStyle } from "../../Enums/UiEnums";
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import { ButtonDisplayStyle } from "../../Enums/UiEnums";
    import vSelect from 'vue-select';
    import { useListStore } from '../../lib/Stores/ListStore';
    import { initPinia } from '../../lib/PiniaMgr';
    import { isValidPhone, isValidEmail, isValidURL } from '../../lib/Validation';
    import '../../../css/choice.css';
    import { TextToPayCompanySettings } from '../../lib/Models/PaySimple/PaySimpleTextToPayModel';
    import { ICompanySetting } from '../../lib/Models/CompanySettingModel';
    import { CountryCode } from '../../Enums/ListEnums';
    import * as choiceApi from '../../lib/ChoiceApi';
    import { IGeographicalStateOrProvinceModel } from '../../lib/Api/List';

    let formData = reactive<{
        accessEnabled?: boolean,
        name?: string,
        phone?: string,
        email?: string,
        website?: string,
        address: {
            line1?: string,
            line2?: string,
            city?: string,
            state?: string,
            zip?: string
        },
        error?: string[]
        success?: string
    }>({
        name: undefined,
        phone: undefined,
        email: undefined,
        website: undefined,
        address: {
            line1: undefined,
            line2: undefined,
            city: undefined,
            state: undefined,
            zip: undefined
        },
        error: undefined,
        success: undefined
    });

    const listStore = useListStore(initPinia());
    listStore.getGeographicalStates();

    onMounted(async () => {
        const screenAccess = await choiceApi.user.useScreen('TextToPay');
        if (!screenAccess || !screenAccess.use) { formData.accessEnabled = false; return;}

        formData.accessEnabled = true;
        const ttpCompanySettings = await choiceApi.companySettings.getCompanySettingByParameterName('PS');
        if (ttpCompanySettings) {
            formData.name = ttpCompanySettings[TextToPayCompanySettings.Name].parameterValue,
            formData.address = {
                line1: ttpCompanySettings[TextToPayCompanySettings.AddressLine1].parameterValue,
                line2: ttpCompanySettings[TextToPayCompanySettings.AddressLine2].parameterValue,
                city: ttpCompanySettings[TextToPayCompanySettings.AddressCity].parameterValue,
                state: ttpCompanySettings[TextToPayCompanySettings.AddressState].parameterValue,
                zip: ttpCompanySettings[TextToPayCompanySettings.AddressZip].parameterValue
            },
            formData.phone = ttpCompanySettings[TextToPayCompanySettings.Phone].parameterValue,
            formData.email = ttpCompanySettings[TextToPayCompanySettings.Email].parameterValue,
            formData.website = ttpCompanySettings[TextToPayCompanySettings.Website].parameterValue;
        }
    });

    const handleSelectionChange = (value: any) => {
        formData.address.state = value.state_code;
    };

    async function UpdateSettings() {
        formData.success = undefined;
        formData.error = undefined;
        let errorMessage: string[] = [];

        if (!formData.name) {
            errorMessage.push("A name must be provided");
        }
        if (!formData.phone) {
            errorMessage.push("A phone number must be provided");
        }
        if (formData.phone && (!isValidPhone(formData.phone) || /[^()\-\s\d]/.test(formData.phone))) {
            errorMessage.push("Invalid phone number provided");
        }
        if (formData.email && !isValidEmail(formData.email)) {
            errorMessage.push("Invalid email provided");
        }
        if (formData.website && !isValidURL(formData.website)) {
            errorMessage.push("Invalid website provided");
        }
        if (Object.keys(formData.address).some(key => !formData.address[key as keyof typeof formData.address] && key != 'line2')) {
            errorMessage.push("A complete address must be provided");
        }
        formData.error = errorMessage;
        
        if (errorMessage.length == 0) {
            const parameterName = 'PS';
            const companySettings: ICompanySetting[] = [
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.Name,
                    parameterValue: formData.name
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.AddressLine1,
                    parameterValue: formData.address.line1
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.AddressLine2,
                    parameterValue: formData.address.line2 ?? ''
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.AddressCity,
                    parameterValue: formData.address.city
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.AddressState,
                    parameterValue: formData.address.state ?? ''
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.AddressZip,
                    parameterValue: formData.address.zip
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.Phone,
                    parameterValue: formData.phone
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.Email,
                    parameterValue: formData.email ?? ''
                },
                {
                    parameterName: parameterName,
                    parameterOrder: TextToPayCompanySettings.Website,
                    parameterValue: formData.website ?? ''
                }
            ];
            const error = await choiceApi.companySettings.setCompanySettings(parameterName, companySettings);
            if (!error) {
                formData.success = "Successfully updated Text to Pay settings";
                setTimeout(() => { formData.success = "" }, 2000);
            } else {
                formData.error.push("An error occurred; failed to update Text to Pay settings");
            }
        }
    }

</script>

<style>
    .ttpSetupContainer {
        margin-left: 1rem;
    }
    .TextToPaySetupLabel {
        width: 8rem;
    }
    .Input {
        width: 20rem;
    }
    .AddressInput {
        width: 16rem;
    }
    #stateSelect {
        font-size: .75rem;
    }
    .vs__selected-options {
        padding-left: 0.4rem !important; /* Adjust the font size as needed */
    }
    .vs__clear {
        margin-bottom: 0.3rem;
    }
</style>

<template>
    <div class="ttpSetupContainer" v-if="formData.accessEnabled == false">
        <div class="row">
            <div class="mt-2 mb-4 label-isalus-small">
                <div style="width: 26rem; margin-bottom: 1rem; font-weight: bold">This feature has not yet been enabled.</div>
            </div>
        </div>
    </div>
    <div class="ttpSetupContainer" v-if="formData.accessEnabled">
        <div class="row">
            <div class="mt-2 mb-4 label-isalus-small">
                <div style="width: 26rem; margin-bottom: 1rem; font-weight: bold">Set the configuration for your company's Text to Pay link. These values will be displayed in the payment checkout.</div>
            </div>
        </div>
        <div class="row mt-2">
            <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                Company Name
            </div>
            <div class="Input">
                <TextInput 
                    v-model:text-value="formData.name"
                    :maxLength="50"
                    cssFramework="vanilla">
                </TextInput>
            </div>
        </div>
        <div class="row mt-2">
            <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                Company Phone
            </div>
            <div class="Input">
                <TextInput 
                    v-model:text-value="formData.phone" 
                    :maxLength="20"
                    :cssFramework="'vanilla'">
                </TextInput>
            </div>
        </div>
        <div class="row mt-2">
            <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                Company Email
            </div>
            <div class="Input">
                <TextInput 
                    v-model:text-value="formData.email" 
                    :maxLength="50"
                    :cssFramework="'vanilla'">
                </TextInput>
            </div>
        </div>
        <div class="row mt-2">
            <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                Company Website
            </div>
            <div class="Input">
                <TextInput 
                    v-model:text-value="formData.website" 
                    :maxLength="50"
                    :cssFramework="'vanilla'">
                </TextInput>
            </div>
        </div>
        <div class="mt-3 label-isalus-small">
            Company Address
        </div>
        <div class="row mt-2" style="margin-left: 10px">
            <div class="row mt-2">
                <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                    Address Line 1
                </div>
                <div class="AddressInput">
                    <TextInput 
                        v-model:text-value="formData.address.line1" 
                        :maxLength="50"
                        :cssFramework="'vanilla'">
                    </TextInput>
                </div>
            </div>
            <div class="row mt-2">
                <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                    Address Line 2
                </div>
                <div class="AddressInput">
                    <TextInput 
                        v-model:text-value="formData.address.line2" 
                        :maxLength="30"
                        :cssFramework="'vanilla'">
                    </TextInput>
                </div>
            </div>
            <div class="row mt-2 ">
                <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                    City
                </div>
                <div class="AddressInput">
                    <TextInput 
                        v-model:text-value="formData.address.city" 
                        :maxLength="30"
                        :cssFramework="'vanilla'">
                    </TextInput>
                </div>
            </div>
            <div class="row mt-2 ">
                <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                    State
                </div>
                <div class="AddressInput">
                    <v-select
                        id="stateSelect"
                        v-model.value="formData.address.state"
                        :options="listStore.geographicalStatesAndProvinces?.filter(state => state.country_code == CountryCode.USA)"
                        @option:selected="handleSelectionChange"
                        label="state_code"
                    >
                        <template #option="option">
                            {{ (option as IGeographicalStateOrProvinceModel).state_code }}
                        </template>
                    </v-select>
                </div>
            </div>
            <div class="row mt-2 ">
                <div class="TextToPaySetupLabel mt-2 label-isalus-small">
                    Zip
                </div>
                <div class="AddressInput">
                    <TextInput 
                        v-model:text-value="formData.address.zip" 
                        :maxLength="12"
                        :cssFramework="'vanilla'">
                    </TextInput>
                </div>
            </div>
        </div>
        <div class="row mt-5" style="display: flex; justify-content: end; width: 450px">
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Save"
                    :base-text="'Save Settings'"
                    :text="'Save Settings'"
                    :size="ButtonDisplaySize.Small" 
                    :spinText="'Saving'"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="UpdateSettings"
                />
            </div>
        </div> 
        <div class="row mt-2">
            <div class="col mt-2 label-isalus-small">
                <MessageSection v-if="formData.error?.length" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    <div v-for="(item, index) in formData.error" :key="index">
                        {{ item }}
                    </div>
                </MessageSection>
                <MessageSection v-if="formData.success" :type="MessageSectionType.Text" :display-style="DisplayStyle.Success">
                    {{formData.success}}
                </MessageSection>
            </div>
        </div>
    </div>
</template>
